/**
 * The tasks belonging to one area or project, with a way to add another.
 *
 * Shared by "Gone quiet" and the Areas/Projects page, because they are the same question
 * asked from two directions: *what is in here, and what should be?*
 *
 * The add box is the point as much as the list is. Opening a container to see nothing in it
 * is the moment you think of the thing that should be — and if adding it means going back
 * to the top of the page and picking the container out of a dropdown again, the thought is
 * usually gone by the time you get there.
 */

import { useCallback, useEffect, useState } from "react";
import { api, type Area, type Project, type Task } from "../api";
import { TaskList, type TaskActions } from "./TaskList";
import { anchorFor, mergeHeld, type HeldTask } from "./justDone";

export function TaskDrawer({
  kind,
  containerId,
  containerName,
  today,
  areas,
  projects,
  actions,
  onChanged,
  refreshKey,
}: {
  kind: "area" | "project";
  containerId: string;
  containerName: string;
  today: string;
  areas: Area[];
  projects: Project[];
  actions: TaskActions;
  /** Called after anything changes, so the page around this can refresh its counts. */
  onChanged?: () => void | Promise<void>;
  /**
   * Bumped whenever the page around this drawer reloads. Without it the drawer kept
   * whatever it fetched when it was opened, so acting on a task anywhere else left it
   * showing a list that was true a minute ago.
   */
  refreshKey?: number;
}) {
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [justDone, setJustDone] = useState<HeldTask[]>([]);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");

  const fetchTasks = useCallback(async () => {
    // Fetched rather than filtered from whatever list is on screen: those are capped and
    // exclude starred and snoozed tasks, so filtering one would under-report what is here.
    try {
      const body = await api.tasks(
        kind === "area"
          ? { area: containerId, limit: 100 }
          : { project: containerId, limit: 100 },
      );
      setTasks(body.tasks);
    } catch {
      setTasks([]);
    }
  }, [kind, containerId]);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks, refreshKey]);

  const shown = mergeHeld(tasks ?? [], justDone);

  /** Act, then refresh this drawer as well as the page around it. */
  const wrap = <A extends unknown[]>(fn?: (...args: A) => void | Promise<void>) =>
    fn &&
    (async (...args: A) => {
      await fn(...args);
      await fetchTasks();
      await onChanged?.();
    });

  /** `wrap` for the calls whose answer the row needs: the steps a breakdown made, to offer taking
   * them back. */
  const through = <A extends unknown[], R>(fn?: (...args: A) => Promise<R>) =>
    fn &&
    (async (...args: A) => {
      const result = await fn(...args);
      await fetchTasks();
      await onChanged?.();
      return result;
    });

  const local: TaskActions = {
    // Same rule as the briefing: a ticked task stays visible until this list is rebuilt, so
    // an accidental tick can be taken back. Nothing else can reach a completed task.
    onComplete: async (task, at) => {
      // Anchored before the refetch, so the row holds its place instead of sliding to the
      // end of the list and out from under the cursor.
      const after = anchorFor(shown, task);
      await actions.onComplete(task, at);
      setJustDone((current) => [
        ...current.filter((h) => h.task.id !== task.id),
        { task: { ...task, status: "done" }, after },
      ]);
      await fetchTasks();
      await onChanged?.();
    },
    onUndo: async (task) => {
      await actions.onUndo?.(task);
      setJustDone((current) => current.filter((h) => h.task.id !== task.id));
      await fetchTasks();
      await onChanged?.();
    },
    onDefer: wrap(actions.onDefer),
    onStar: wrap(actions.onStar),
    onEdit: wrap(actions.onEdit) as TaskActions["onEdit"],
    onDrop: wrap(actions.onDrop),
    onDelete: wrap(actions.onDelete) as TaskActions["onDelete"],
    // Breaking a task down is the same act here as in the Tasks card: the split button opens its
    // form, and beside it the AI bulb (shown only where the server has AI, `AiBreakdownButton`).
    // These were missing, so a task listed in a project had neither.
    onBreakdown: through(actions.onBreakdown),
    onAiBreakdown: through(actions.onAiBreakdown),
    onUndoBreakdown: through(actions.onUndoBreakdown),
    onSetNextAction: through(actions.onSetNextAction),
    onReorderSubtasks: through(actions.onReorderSubtasks),
  };

  async function add(event: React.FormEvent) {
    event.preventDefault();
    const text = title.trim();
    if (!text) return;
    setBusy(true);
    setTitle("");
    try {
      await api.createTask({
        title: text,
        ...(kind === "area" ? { area_id: containerId } : { project_id: containerId }),
      });
      await fetchTasks();
      await onChanged?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="drawer">
      {tasks === null ? (
        <p className="muted small">Loading…</p>
      ) : (
        <TaskList
          tasks={shown}
          today={today}
          areas={areas}
          projects={projects}
          actions={local}
          empty="Nothing open here."
        />
      )}

      <form className="row" onSubmit={add} style={{ marginTop: "0.5rem" }}>
        <input
          className="input"
          placeholder={`Add to ${containerName} - title, enter, done`}
          aria-label={`New task in ${containerName}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <button className="btn primary small" type="submit" disabled={busy || !title.trim()}>
          Add
        </button>
      </form>
    </div>
  );
}
